import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip, Switch, Tabs, Tab } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CategoryIcon from '@mui/icons-material/Category';
import RestoreIcon from '@mui/icons-material/Restore';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import LabelIcon from '@mui/icons-material/Label';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useSnackbar } from 'notistack';
import debounce from 'lodash/debounce';

import accountTypeService, {
  AccountType,
  CreateAccountTypeDto,
  UpdateAccountTypeDto
} from '../services/accountTypeService';
import activityAreaService, { ActivityArea } from '../services/activityAreaService';
import categoryNavService, { Category as NavCategory } from '../services/category.service';
import imageUploadService from '../services/imageUpload.service';
import EntityHistoryButton from '../components/common/EntityHistoryButton';
import FieldEditor from '../components/common/FieldEditor';
import { ConfirmDialog } from '../components/common';

import './EntityManagement.css';

interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

const AccountTypeManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [accountTypes, setAccountTypes] = useState<AccountType[]>([]);
  const [selectedAccountType, setSelectedAccountType] = useState<AccountType | null>(null);
  const [activityAreas, setActivityAreas] = useState<ActivityArea[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ total: 0, page: 1, limit: 10, pages: 0 });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');

  // Dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<AccountType | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [currentTab, setCurrentTab] = useState(0);

  // Form
  const [formData, setFormData] = useState<Partial<AccountType> & { activityArea?: string }>({
    name: '', description: '', activityArea: '', isActive: true, isDefault: false, fields: [],
  });
  const [imageUploading, setImageUploading] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Categories management
  const [categories, setCategories] = useState<string[]>([]);
  const [categoryInput, setCategoryInput] = useState('');
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [showCategoryPanel, setShowCategoryPanel] = useState(false);
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editingCategoryValue, setEditingCategoryValue] = useState('');

  // New navigation categories (L2 leaves of mega-menu Category tree)
  const [navCategories, setNavCategories] = useState<NavCategory[]>([]);

  const loadAccountTypes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await accountTypeService.getAccountTypes(
        pagination.page,
        pagination.limit,
        debouncedSearchTerm,
        !showInactive ? undefined : false,
        categoryFilter || undefined
      );
      setAccountTypes(response.data);
      const validated = {
        total: response.pagination.total || 0,
        page: Math.min(response.pagination.page || 1, response.pagination.pages || 1),
        limit: response.pagination.limit || 10,
        pages: response.pagination.pages || 1
      };
      if (JSON.stringify(pagination) !== JSON.stringify(validated)) setPagination(validated);
    } catch (error) {
      console.error('Failed to load account types:', error);
      enqueueSnackbar('خطا در بارگذاری انواع محصول', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, debouncedSearchTerm, showInactive, categoryFilter]);

  useEffect(() => {
    const d = debounce(() => setDebouncedSearchTerm(searchTerm), 500);
    d();
    return () => d.cancel();
  }, [searchTerm]);

  useEffect(() => { loadAccountTypes(); }, [loadAccountTypes]);

  useEffect(() => {
    (async () => {
      try {
        const areas = await activityAreaService.getActiveActivityAreas();
        setActivityAreas(areas);
      } catch (e) { console.error('Failed to load activity areas:', e); }
    })();
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const cats = await accountTypeService.getCategories();
      setCategories(cats);
    } catch (e) { console.error('Failed to load categories:', e); }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  useEffect(() => {
    (async () => {
      try {
        const list = await categoryNavService.list({ includeDeleted: false });
        setNavCategories(list);
      } catch (e) { console.error('Failed to load nav categories:', e); }
    })();
  }, []);

  const handleAddCategory = async () => {
    const name = categoryInput.trim();
    if (!name) return;
    setCategoryLoading(true);
    try {
      const updated = await accountTypeService.createCategory(name);
      setCategories(updated);
      setCategoryInput('');
      enqueueSnackbar('دسته‌بندی اضافه شد', { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در افزودن دسته‌بندی', { variant: 'error' });
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleDeleteCategory = async (name: string) => {
    setCategoryLoading(true);
    try {
      const updated = await accountTypeService.deleteCategory(name);
      setCategories(updated);
      enqueueSnackbar('دسته‌بندی حذف شد', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('خطا در حذف دسته‌بندی', { variant: 'error' });
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleStartEditCategory = (cat: string) => {
    setEditingCategory(cat);
    setEditingCategoryValue(cat);
  };

  const handleCancelEditCategory = () => {
    setEditingCategory(null);
    setEditingCategoryValue('');
  };

  const handleSaveRenameCategory = async () => {
    const newName = editingCategoryValue.trim();
    if (!newName || !editingCategory || newName === editingCategory) {
      handleCancelEditCategory();
      return;
    }
    setCategoryLoading(true);
    try {
      const updated = await accountTypeService.renameCategory(editingCategory, newName);
      setCategories(updated);
      enqueueSnackbar('دسته‌بندی ویرایش شد', { variant: 'success' });
      handleCancelEditCategory();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در ویرایش دسته‌بندی', { variant: 'error' });
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    try {
      const result = await imageUploadService.uploadImage(file, 'products');
      if (result?.url) {
        setFormData(prev => ({ ...prev, image: result.url }));
        enqueueSnackbar('تصویر با موفقیت آپلود شد', { variant: 'success' });
      } else {
        enqueueSnackbar('خطا در آپلود تصویر', { variant: 'error' });
      }
    } catch {
      enqueueSnackbar('خطا در آپلود تصویر', { variant: 'error' });
    } finally {
      setImageUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPagination({ ...pagination, page: 1 });
  };
  const handleKeyPress = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleSearch(); };

  const handleOpenCreateDialog = () => {
    setFormData({ name: '', description: '', image: '', category: '', categoryIds: [], activityArea: '', isActive: true, isDefault: false, fields: [] });
    setSelectedAccountType(null);
    setDialogMode('create');
    setCurrentTab(0);
    setFormError('');
    setOpenDialog(true);
  };

  const handleOpenEditDialog = async (accountType: AccountType) => {
    try {
      setLoading(true);
      let fullAccountType = await accountTypeService.getAccountType(accountType.id);
      if (!fullAccountType || !fullAccountType.id) throw new Error('داده ناقص');
      if (!fullAccountType.fields) fullAccountType.fields = accountType.fields || [];

      const rawArea = (fullAccountType as any).activityArea;
      let areaId = '';
      if (rawArea) {
        if (typeof rawArea === 'string') areaId = rawArea;
        else if (rawArea._id) areaId = rawArea._id.toString();
      }

      setSelectedAccountType(fullAccountType);
      setFormData({
        id: fullAccountType.id,
        name: fullAccountType.name || '',
        description: fullAccountType.description || '',
        image: fullAccountType.image || '',
        category: fullAccountType.category || '',
        categoryIds: Array.isArray(fullAccountType.categoryIds)
          ? fullAccountType.categoryIds.map((c: any) =>
              typeof c === 'string' ? c : String(c?._id || c?.id || c)
            )
          : [],
        activityArea: areaId,
        isActive: fullAccountType.isActive !== undefined ? fullAccountType.isActive : true,
        isDefault: fullAccountType.isDefault || false,
        fields: JSON.parse(JSON.stringify(fullAccountType.fields || [])),
      });
      setDialogMode('edit');
      setCurrentTab(0);
      setFormError('');
      setOpenDialog(true);
    } catch (error) {
      console.error('Failed to load account type:', error);
      enqueueSnackbar('خطا در بارگذاری جزئیات', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAccountType(null);
    setCurrentTab(0);
  };

  const handleSubmit = async () => {
    if (!formData.name) { setFormError('لطفاً نام را وارد کنید'); return; }
    try {
      setSubmitLoading(true);
      if (dialogMode === 'create') {
        const createData: CreateAccountTypeDto = {
          name: formData.name || '',
          description: formData.description || '',
          image: formData.image || undefined,
          category: formData.category || undefined,
          categoryIds: Array.isArray(formData.categoryIds)
            ? formData.categoryIds.map((c: any) =>
                typeof c === 'string' ? c : String(c?._id || c?.id || c)
              )
            : undefined,
          activityArea: formData.activityArea || undefined,
          isActive: formData.isActive !== undefined ? formData.isActive : true,
          fields: formData.fields || [],
        };
        await accountTypeService.createAccountType(createData);
        enqueueSnackbar('نوع محصول با موفقیت ایجاد شد', { variant: 'success' });
      } else {
        if (!selectedAccountType?.id) return;
        const updateData: UpdateAccountTypeDto = {
          name: formData.name || '',
          description: formData.description || '',
          image: formData.image || undefined,
          category: formData.category || undefined,
          categoryIds: Array.isArray(formData.categoryIds)
            ? formData.categoryIds.map((c: any) =>
                typeof c === 'string' ? c : String(c?._id || c?.id || c)
              )
            : undefined,
          activityArea: formData.activityArea || undefined,
          isActive: Boolean(formData.isActive),
          fields: Array.isArray(formData.fields) ? JSON.parse(JSON.stringify(formData.fields)) : [],
        };
        await accountTypeService.updateAccountType(selectedAccountType.id, updateData);
        enqueueSnackbar('نوع محصول با موفقیت به‌روزرسانی شد', { variant: 'success' });
      }
      handleCloseDialog();
      loadAccountTypes();
    } catch (error) {
      console.error('Failed to save:', error);
      enqueueSnackbar('خطا در ذخیره', { variant: 'error' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleOpenDeleteDialog = (a: AccountType) => { setAccountToDelete(a); setOpenDeleteDialog(true); };
  const handleCloseDeleteDialog = () => { setOpenDeleteDialog(false); setAccountToDelete(null); };

  const handleDelete = async () => {
    if (!accountToDelete) return;
    try {
      setSubmitLoading(true);
      await accountTypeService.deleteAccountType(accountToDelete.id);
      enqueueSnackbar('نوع محصول حذف شد', { variant: 'success' });
      handleCloseDeleteDialog();
      loadAccountTypes();
    } catch (e) {
      enqueueSnackbar('خطا در حذف', { variant: 'error' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleRestore = async (a: AccountType) => {
    try {
      await accountTypeService.restoreAccountType(a.id);
      enqueueSnackbar('بازیابی شد', { variant: 'success' });
      loadAccountTypes();
    } catch (e) { enqueueSnackbar('خطا در بازیابی', { variant: 'error' }); }
  };

  const handleToggleStatus = async (a: AccountType) => {
    try {
      await accountTypeService.toggleAccountTypeStatus(a.id);
      enqueueSnackbar('وضعیت تغییر کرد', { variant: 'success' });
      loadAccountTypes();
    } catch (e) { enqueueSnackbar('خطا در تغییر وضعیت', { variant: 'error' }); }
  };

  const getAreaName = (a: AccountType) => {
    const raw = (a as any).activityArea;
    if (!raw) return '-';
    if (typeof raw === 'object' && raw.name) return raw.name;
    const id = typeof raw === 'string' ? raw : raw._id?.toString();
    return activityAreas.find(ar => ar._id === id)?.name || 'نامشخص';
  };

  const clearFilters = () => {
    setSearchTerm(''); setSearchInput(''); setShowInactive(false); setCategoryFilter('');
    setPagination({ ...pagination, page: 1 });
  };

  // Group nav categories L1 → L2 children for the filter dropdown
  const groupedNavCategories = useMemo(() => {
    const l1s = navCategories.filter(c => c.level === 1 && !c.isDeleted);
    const byParent: Record<string, NavCategory[]> = {};
    navCategories.forEach(c => {
      if (c.level === 2 && !c.isDeleted && c.parent) {
        const pid = String(c.parent);
        (byParent[pid] ||= []).push(c);
      }
    });
    return l1s
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(l1 => ({
        l1,
        l2s: (byParent[String(l1._id)] || []).sort((a, b) => (a.order || 0) - (b.order || 0)),
      }));
  }, [navCategories]);

  const selectedCategoryLabel = useMemo(() => {
    if (!categoryFilter) return '';
    const cat = navCategories.find(c => String(c._id) === categoryFilter);
    if (!cat) return '';
    if (cat.level === 1) return cat.name;
    const parent = navCategories.find(c => String(c._id) === String(cat.parent));
    return parent ? `${parent.name} ▸ ${cat.name}` : cat.name;
  }, [categoryFilter, navCategories]);

  const activeCount = accountTypes.filter(a => a.isActive).length;
  const inactiveCount = accountTypes.filter(a => !a.isActive).length;
  const totalPages = pagination.pages;
  const startItem = (pagination.page - 1) * pagination.limit + 1;
  const endItem = Math.min(pagination.page * pagination.limit, pagination.total);
  const hasActiveFilters = searchTerm || showInactive || categoryFilter;

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت انواع محصول</h1>
          <p className="entity-subtitle">تعریف انواع محصول و فیلدهای داینامیک آن‌ها</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={() => loadAccountTypes()} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
          <button
            className={`header-btn ${showCategoryPanel ? 'active' : ''}`}
            onClick={() => setShowCategoryPanel(p => !p)}
            title="مدیریت دسته‌بندی‌ها"
            style={{ gap: 6 }}
          >
            <LabelIcon fontSize="small" />
            دسته‌بندی‌ها
          </button>
          <button className="header-btn primary" onClick={handleOpenCreateDialog}>
            <AddIcon fontSize="small" />
            ایجاد نوع محصول
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><CategoryIcon /></div>
          <div>
            <p className="entity-stat-label">کل انواع</p>
            <p className="entity-stat-value">{pagination.total}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">فعال</p>
            <p className="entity-stat-value">{activeCount}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><BlockIcon /></div>
          <div>
            <p className="entity-stat-label">غیرفعال</p>
            <p className="entity-stat-value">{inactiveCount}</p>
          </div>
        </div>
      </div>

      {/* Category Management Panel */}
      {showCategoryPanel && (
        <div className="edit-card" style={{ margin: '0 0 16px 0' }}>
          <div className="edit-card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <LabelIcon style={{ fontSize: 16 }} />
            مدیریت دسته‌بندی‌های نوع محصول
          </div>
          <div className="edit-card-body">
            {/* Add new */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                type="text"
                className="filter-field"
                style={{ flex: 1 }}
                placeholder="نام دسته‌بندی جدید (مثلاً: مراقبت از پوست)"
                value={categoryInput}
                onChange={e => setCategoryInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddCategory(); }}
              />
              <button
                className="filter-btn"
                onClick={handleAddCategory}
                disabled={categoryLoading || !categoryInput.trim()}
                style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
              >
                {categoryLoading ? <CircularProgress size={14} /> : <AddCircleOutlineIcon style={{ fontSize: 16 }} />}
                افزودن
              </button>
            </div>
            {/* List */}
            {categories.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--color-text-muted)', textAlign: 'center', padding: '8px 0' }}>
                هنوز دسته‌بندی تعریف نشده
              </p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {categories.map(cat => (
                  editingCategory === cat ? (
                    <span key={cat} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input
                        type="text"
                        className="filter-field"
                        style={{ width: 160, height: 28, fontSize: 13, padding: '2px 8px' }}
                        value={editingCategoryValue}
                        onChange={e => setEditingCategoryValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSaveRenameCategory();
                          if (e.key === 'Escape') handleCancelEditCategory();
                        }}
                        autoFocus
                      />
                      <button
                        className="filter-btn"
                        onClick={handleSaveRenameCategory}
                        disabled={categoryLoading || !editingCategoryValue.trim()}
                        style={{ padding: '2px 8px', fontSize: 12, height: 28 }}
                      >
                        {categoryLoading ? <CircularProgress size={12} /> : '✓'}
                      </button>
                      <button
                        className="filter-btn"
                        onClick={handleCancelEditCategory}
                        style={{ padding: '2px 6px', fontSize: 12, height: 28, background: 'none' }}
                      >
                        ✕
                      </button>
                    </span>
                  ) : (
                    <span key={cat} className="tag tag-sm tag-platform" style={{ display: 'flex', alignItems: 'center', gap: 4, paddingRight: 10 }}>
                      {cat}
                      <button
                        onClick={() => handleStartEditCategory(cat)}
                        disabled={categoryLoading}
                        title="ویرایش"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: '0 2px', fontSize: 12, lineHeight: 1, opacity: 0.65 }}
                      >
                        ✎
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(cat)}
                        disabled={categoryLoading}
                        title="حذف"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: '0 2px', fontSize: 14, lineHeight: 1, opacity: 0.65 }}
                      >
                        ×
                      </button>
                    </span>
                  )
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجو در نام، توضیحات یا دسته‌بندی منو..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">
              <CategoryIcon fontSize="small" />
              دسته‌بندی منو
            </label>
            <select
              className="filter-field"
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPagination({ ...pagination, page: 1 });
              }}
              style={{ height: 40 }}
            >
              <option value="">همه دسته‌ها</option>
              {groupedNavCategories.map(({ l1, l2s }) => (
                <optgroup key={String(l1._id)} label={l1.name}>
                  <option value={String(l1._id)}>{l1.name} — همه</option>
                  {l2s.map(l2 => (
                    <option key={String(l2._id)} value={String(l2._id)}>
                      &nbsp;&nbsp;{l2.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">نمایش</label>
            <label className="filter-toggle" style={{ height: 40 }}>
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => { setShowInactive(e.target.checked); setPagination({ ...pagination, page: 1 }); }}
              />
              نمایش غیرفعال‌ها
            </label>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchTerm && (
              <span className="filter-tag">
                جستجو: {searchTerm}
                <button onClick={() => { setSearchTerm(''); setSearchInput(''); }}>×</button>
              </span>
            )}
            {showInactive && (
              <span className="filter-tag">
                شامل غیرفعال‌ها
                <button onClick={() => setShowInactive(false)}>×</button>
              </span>
            )}
            {categoryFilter && (
              <span className="filter-tag">
                دسته: {selectedCategoryLabel}
                <button onClick={() => setCategoryFilter('')}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : accountTypes.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><CategoryIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">هیچ نوع محصولی یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>نام</th>
                  <th>حوزه فعالیت</th>
                  <th>فیلدها</th>
                  <th>وضعیت</th>
                  <th>تاریخ ایجاد</th>
                  <th style={{ width: 180 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {accountTypes.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <div className="entity-info-cell">
                        <div className="entity-image">
                          {a.image
                            ? <img src={a.image} alt={a.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                            : <CategoryIcon />}
                        </div>
                        <div className="entity-info">
                          <span className="entity-name">{a.name}</span>
                          {a.description && <span className="entity-desc">{a.description}</span>}
                          {Array.isArray(a.categoryIds) && a.categoryIds.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                              {a.categoryIds.map((c: any, i: number) => {
                                if (typeof c === 'string' || !c) return null;
                                const parent =
                                  c.parent && typeof c.parent === 'object' ? c.parent.name : null;
                                return (
                                  <span
                                    key={(c._id || c.id || i).toString()}
                                    className="tag tag-sm tag-platform"
                                    style={{ fontSize: 10, padding: '2px 8px', lineHeight: 1.4 }}
                                    title={parent ? `${parent} ▸ ${c.name}` : c.name}
                                  >
                                    {parent ? `${parent} ▸ ${c.name}` : c.name}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="tag tag-sm tag-platform">{getAreaName(a)}</span>
                    </td>
                    <td>
                      <span className="tag tag-sm tag-admin">{a.fields?.length || 0} فیلد</span>
                    </td>
                    <td>
                      <span className={`tag tag-sm ${a.isActive ? 'tag-active' : 'tag-inactive'}`}>
                        {a.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td>{new Date(a.createdAt).toLocaleDateString('fa-IR')}</td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="ویرایش" arrow>
                          <button className="action-btn edit" onClick={() => handleOpenEditDialog(a)}>
                            <EditIcon />
                          </button>
                        </Tooltip>
                        {a.isActive ? (
                          <Tooltip title="حذف" arrow>
                            <button className="action-btn delete" onClick={() => handleOpenDeleteDialog(a)}>
                              <DeleteIcon />
                            </button>
                          </Tooltip>
                        ) : (
                          <Tooltip title="بازگردانی" arrow>
                            <button className="action-btn toggle" onClick={() => handleRestore(a)}>
                              <RestoreIcon />
                            </button>
                          </Tooltip>
                        )}
                        <Tooltip title={a.isActive ? 'غیرفعال کردن' : 'فعال کردن'} arrow>
                          <button
                            className={`action-btn toggle ${a.isActive ? 'active' : ''}`}
                            onClick={() => handleToggleStatus(a)}
                          >
                            {a.isActive ? <ToggleOnIcon /> : <ToggleOffIcon />}
                          </button>
                        </Tooltip>
                        <EntityHistoryButton
                          entityType="AccountType"
                          entityId={a.id}
                          size="small"
                          variant="text"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {accountTypes.map((a) => (
                <div key={a.id} className={`entity-card ${!a.isActive ? 'inactive' : ''}`}>
                  <div className="entity-card-header">
                    <div className="entity-card-image">
                      {a.image
                        ? <img src={a.image} alt={a.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                        : <CategoryIcon />}
                    </div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">{a.name}</div>
                      <div className="entity-card-desc">
                        {getAreaName(a)} • {a.fields?.length || 0} فیلد
                      </div>
                    </div>
                    <div className="entity-card-status">
                      <span className={`tag tag-sm ${a.isActive ? 'tag-active' : 'tag-inactive'}`}>
                        {a.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                    </div>
                  </div>
                  <div className="entity-card-actions">
                    <button className="entity-card-btn primary" onClick={() => handleOpenEditDialog(a)}>
                      <EditIcon fontSize="small" /> ویرایش
                    </button>
                    <button className="entity-card-btn" onClick={() => handleToggleStatus(a)}>
                      {a.isActive ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" />}
                      {a.isActive ? 'غیرفعال' : 'فعال'}
                    </button>
                    {a.isActive ? (
                      <button className="entity-card-btn danger" onClick={() => handleOpenDeleteDialog(a)}>
                        <DeleteIcon fontSize="small" /> حذف
                      </button>
                    ) : (
                      <button className="entity-card-btn" onClick={() => handleRestore(a)}>
                        <RestoreIcon fontSize="small" /> بازیابی
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {startItem} تا {endItem} از {pagination.total}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={pagination.limit}
                  onChange={(e) => setPagination({ ...pagination, limit: parseInt(e.target.value), page: 1 })}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <button
                  className="pagination-btn"
                  onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                  disabled={pagination.page >= totalPages}
                >
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>{pagination.page} / {totalPages || 1}</span>
                <button
                  className="pagination-btn"
                  onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                  disabled={pagination.page <= 1}
                >
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth className="accounttype-edit-dialog">
        <div className="edit-dialog-title">
          <span>{dialogMode === 'create' ? 'ایجاد نوع محصول جدید' : 'ویرایش نوع محصول'}</span>
          <button className="details-close" onClick={handleCloseDialog}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <div style={{ borderBottom: '1px solid var(--color-border)' }}>
          <Tabs value={currentTab} onChange={(_, v) => setCurrentTab(v)} sx={{ px: 2 }}>
            <Tab label="اطلاعات پایه" />
            <Tab label="فیلدها" />
          </Tabs>
        </div>
        <DialogContent className="edit-dialog-content">
          {formError && <div className="dialog-error">{formError}</div>}

          {currentTab === 0 && (
            <div className="edit-form">
              {/* Basic Info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات اصلی</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نام <span style={{ color: '#dc2626' }}>*</span></label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="مثلاً: کرم آبرسان"
                      />
                    </div>
                    <div className="edit-field">
                      <label>حوزه فعالیت</label>
                      <select
                        value={formData.activityArea || ''}
                        onChange={(e) => setFormData({ ...formData, activityArea: e.target.value })}
                      >
                        <option value="">بدون حوزه فعالیت</option>
                        {activityAreas.map((area) => (
                          <option key={area._id} value={area._id}>{area.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="edit-field">
                      <label>دسته‌بندی (قدیمی - تک‌انتخاب)</label>
                      <select
                        value={formData.category || ''}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      >
                        <option value="">بدون دسته‌بندی</option>
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                      {categories.length === 0 && (
                        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                          ابتدا از دکمه «دسته‌بندی‌ها» دسته‌بندی تعریف کنید
                        </span>
                      )}
                    </div>
                    <div className="edit-field full">
                      <label>دسته‌بندی‌های مگامنو (سطح ۲ — چندانتخابی)</label>
                      {(() => {
                        const l1Map = new Map<string, NavCategory>(navCategories.filter(c => c.level === 1).map(c => [c._id, c]));
                        const l2List = navCategories.filter(c => c.level === 2 && c.parent && c.isActive !== false);
                        const selectedIds = Array.isArray(formData.categoryIds) ? formData.categoryIds : [];
                        const toggle = (id: string) => {
                          const next = selectedIds.includes(id)
                            ? selectedIds.filter(x => x !== id)
                            : [...selectedIds, id];
                          setFormData({ ...formData, categoryIds: next });
                        };
                        if (l2List.length === 0) {
                          return (
                            <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                              هیچ زیرمجموعه‌ای در درخت دسته‌بندی‌ها تعریف نشده. ابتدا از منوی «دسته‌بندی‌ها (مگامنو)» تعریف کنید.
                            </span>
                          );
                        }
                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: 8, border: '1px solid var(--color-border)', borderRadius: 6, maxHeight: 220, overflowY: 'auto' }}>
                            {l2List.map(l2 => {
                              const parent = l1Map.get(String(l2.parent));
                              const checked = selectedIds.includes(l2._id);
                              return (
                                <label
                                  key={l2._id}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '4px 10px',
                                    border: `1px solid ${checked ? '#B25373' : 'var(--color-border)'}`,
                                    borderRadius: 16,
                                    background: checked ? '#FFF4F7' : 'transparent',
                                    cursor: 'pointer',
                                    fontSize: 12,
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggle(l2._id)}
                                    style={{ margin: 0 }}
                                  />
                                  <span>
                                    {parent?.name ? <span style={{ color: '#64748b' }}>{parent.name} / </span> : null}
                                    {l2.name}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        );
                      })()}
                      <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
                        این انتخاب‌ها در مگامنوی سایت ظاهر می‌شوند. هر نوع‌محصول می‌تواند زیر چند زیرمجموعه قرار گیرد.
                      </span>
                    </div>
                    <div className="edit-field full">
                      <label>توضیحات</label>
                      <textarea
                        value={formData.description || ''}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="توضیحات نوع محصول"
                        rows={3}
                      />
                    </div>
                    <div className="edit-field full">
                      <label>تصویر نوع محصول</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {formData.image ? (
                          <div style={{ position: 'relative', width: 96, height: 96, flexShrink: 0 }}>
                            <img
                              src={formData.image}
                              alt="تصویر"
                              style={{ width: 96, height: 96, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--color-border)' }}
                            />
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                              style={{
                                position: 'absolute', top: -8, right: -8,
                                width: 24, height: 24, borderRadius: '50%',
                                background: '#dc2626', color: '#fff', border: 'none',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 14, lineHeight: 1
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <div
                            style={{
                              width: 96, height: 96, borderRadius: 8, flexShrink: 0,
                              border: '2px dashed var(--color-border)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: 'var(--color-text-muted)', background: 'var(--color-bg-secondary)'
                            }}
                          >
                            <CategoryIcon style={{ fontSize: 36 }} />
                          </div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <input
                            ref={imageInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleImageUpload}
                          />
                          <button
                            type="button"
                            className="filter-btn"
                            onClick={() => imageInputRef.current?.click()}
                            disabled={imageUploading}
                            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                          >
                            {imageUploading ? <CircularProgress size={14} /> : <PhotoCameraIcon style={{ fontSize: 16 }} />}
                            {formData.image ? 'تغییر تصویر' : 'آپلود تصویر'}
                          </button>
                          <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                            این تصویر در فرم ایجاد محصول نمایش داده می‌شود
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Settings */}
              <div className="edit-card">
                <div className="edit-card-header">تنظیمات</div>
                <div className="edit-card-body">
                  <div className="edit-switches">
                    <label className="edit-switch-item">
                      <div>
                        <div className="edit-switch-label">وضعیت فعال</div>
                        <div className="edit-switch-desc">نمایش در سایت</div>
                      </div>
                      <Switch size="small" checked={formData.isActive || false}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentTab === 1 && (
            <div className="edit-form">
              <div className="edit-card">
                <div className="edit-card-header">فیلدهای داینامیک</div>
                <div className="edit-card-body">
                  <FieldEditor
                    fields={formData.fields || []}
                    onChange={(fields) => setFormData((prev) => ({ ...prev, fields }))}
                  />
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={handleCloseDialog}>انصراف</button>
          <button className="edit-btn submit" onClick={handleSubmit} disabled={submitLoading || !formData.name}>
            {submitLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            ذخیره
          </button>
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <ConfirmDialog
        open={openDeleteDialog}
        title="حذف نوع محصول"
        message={
          <span>
            آیا از حذف <strong>«{accountToDelete?.name}»</strong> اطمینان دارید؟
            <br />این عملیات غیرقابل بازگشت است.
          </span>
        }
        cancelText="انصراف"
        confirmText="حذف نوع محصول"
        confirmColor="error"
        loading={submitLoading}
        onCancel={handleCloseDeleteDialog}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default AccountTypeManagement;
